import { useEffect, useRef } from "react";
import { usePresenter } from "../../deck/context";
import { fmt, total, totalBudgetMs } from "../../rehearse/clock";
import { pad2, slideTitle } from "../../deck/model";

export function slideKind(model: ReturnType<typeof usePresenter>["model"], i: number): string {
  const s = model.slides[i];
  if (!s) return "";
  return (s.kind === "block" ? s.type : s.kind).toUpperCase();
}

/** Grid of every slide. `cursor` is the keyboard selection; Enter picks it. */
export function Overview({
  current,
  cursor,
  onPick,
}: {
  current: number;
  cursor: number;
  onPick: (i: number) => void;
}) {
  const { model, clock } = usePresenter();
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    grid.current?.querySelector(`[data-index="${cursor}"]`)?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  return (
    <div className="overview" role="dialog" aria-label="Overview">
      <div className="overview-head">
        <span className="eyebrow">Overview · pick a slide</span>
        {clock.started && (
          <span className="timer">
            Rehearsal {fmt(total(clock.times))} / {fmt(totalBudgetMs(model.deck))}
            <button type="button" className="btn" onClick={clock.reset}>
              Reset timer
            </button>
          </span>
        )}
      </div>
      <div className="overview-grid" ref={grid}>
        {model.slides.map((s, i) => (
          <button
            key={i}
            type="button"
            className="thumb"
            data-index={i}
            data-cursor={i === cursor && i !== current}
            aria-current={i === current}
            onClick={() => onPick(i)}
          >
            <span className="thumb-meta">
              {pad2(i + 1)} · {slideKind(model, i)}
            </span>
            {s.kind === "section" && clock.started && <SectionTime sectionId={s.sectionId} />}
            <span className="thumb-title">{slideTitle(model, s)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** On a section card: time spent so far against the section's minutes. */
function SectionTime({ sectionId }: { sectionId: string }) {
  const { model, clock } = usePresenter();
  const minutes = model.sections.get(sectionId)?.section.minutes ?? 0;
  const spent = clock.times[sectionId] ?? 0;
  return (
    <span className="thumb-time" data-over={spent > minutes * 60_000} data-testid={`section-time-${sectionId}`}>
      {fmt(spent)} / {minutes}m
    </span>
  );
}
